import type { ServiceDocument } from '../dto/ServiceDocumentDtos';
import type { BasicFetch } from '../types/basicFetch';
import type { BackendClient } from './backendClient';
import type { ApiCallConfig, AdditionalParams, ApiClient } from '../util/apiClient';
import type { ApiCommand } from '../api/apiCommand';
import type { AuthFromToken } from './supportedAuth';
import { sendApiCommand } from './sendApiCommand';
import { getServiceDocumentUrl } from '../deployment/serviceDocRequest';
import { StaticAuthHeaderSource, callMimirWithFetch, fetchCognitoJwtSource, type AuthHeaderSource } from './mimirCalls';
import { makeError } from '../util/exception';
import type { ServiceCache } from './serviceCache';
import type { Logger } from '../types/logger';

/**
 * Them Mimir endpoint to use.
 */
interface MimirEndpoint {
  type: 'MimirEndpoint';
  /**
   * The HTTP endpoint that is serving out the Mimir API.
   *
   * This is the base URL of the Mimir API, not the service document URL.
   */
  endpointUrl: string;
}

interface CachedServiceDocument {
  type: 'CachedServiceDocument';
  cache: ServiceCache;
}

interface CredentialsSource {
  scheme: 'CredentialsSource';
  source: AuthHeaderSource;
}

interface MimirClientOptions {
  /**
   * How to send HTTP requests.
   */
  fetch: BasicFetch;

  /**
   * How to obtain the service document of the Mimir API backend.
   *
   * TODO: Migrate ServiceCache to common, and offer it as an alternative way to
   * specify the service.
   */
  service: MimirEndpoint | CachedServiceDocument;

  /**
   * How to authenticate the requests.
   *
   * TODO: Support more of the credential types described in supportedAuth.ts.
   */
  credentials: AuthFromToken | CredentialsSource;

  logger: Logger;
}

/**
 * A convenience facade for communicating with the Mimir API backend. This class
 * contains API knowledge in itself, but rather just pulls together all of the
 * pieces defined elsewhere.
 */
export class MimirClient {
  private backend: BackendClient | null = null;

  constructor(private options: MimirClientOptions) {
  }

  async send<OutputType>(command: ApiCommand<OutputType>): Promise<OutputType> {
    if (!this.backend) {
      this.backend = await this.initializeBackendClient();
    }
    return sendApiCommand(this.backend, command);
  }

  async getBackendClient(): Promise<BackendClient> {
    if (!this.backend) {
      this.backend = await this.initializeBackendClient();
    }
    return this.backend;
  }

  getBootstrapUrl(): string {
    if (this.options.service.type === 'CachedServiceDocument') {
      return this.options.service.cache.serviceDocumentUrl;
    }
    return this.options.service.endpointUrl;
  }

  private async initializeBackendClient(): Promise<BackendClient> {
    let sdoc: ServiceDocument;
    const fetch = this.options.fetch;
    if (this.options.service.type === 'CachedServiceDocument') {
      sdoc = await this.options.service.cache.getServiceDocument();
    } else {
      const serviceUrl = getServiceDocumentUrl(this.options.service.endpointUrl);
      const serviceResponse = await fetch(serviceUrl, {});
      try {
        sdoc = await serviceResponse.json();
      } catch (cause) {
        throw makeError('Bad service document', 'BadServiceDocument', { cause, info: { serviceUrl } });
      }
    }

    let authHeaderSource: AuthHeaderSource;
    const credentials = this.options.credentials;
    if (credentials.scheme === 'AuthWithRefreshToken') {
      authHeaderSource = await fetchCognitoJwtSource(this.options.logger, fetch, sdoc, credentials.refreshToken);
    } else if (credentials.scheme === 'AuthWithHttpHeaders') {
      authHeaderSource = new StaticAuthHeaderSource(Object.entries(credentials.httpHeaders));
    } else if (credentials.scheme === 'CredentialsSource') {
      authHeaderSource = credentials.source;
    }

    const logger = this.options.logger;
    const apiClient: ApiClient = {
      async call(clientConfig: ApiCallConfig, url: string, method: string, additionalParams?: AdditionalParams | undefined, bodyUnk?: any): Promise<any> {
        return callMimirWithFetch(logger, fetch, authHeaderSource, url, method, additionalParams, bodyUnk);
      },
      config(): ApiCallConfig {
        return {
          apigHost: sdoc.clientConfig.apigHost,
          apiRegion: sdoc.clientConfig.apiRegion
        };
      },
      refreshAuth(): Promise<void> {
        throw new Error('refreshAuth() function not implemented');
      }
    };
    return {
      getApiClient() {
        return apiClient;
      },
      getServiceLinks() {
        return sdoc.links;
      },
    };
  }
}
